import { Injectable, inject } from '@angular/core';

import { TagCount } from '@cds/betting-offer/tags';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { ClientCachingConfig, TeamPagesConfig, TeamPagesModuleConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { MasterdataApiService } from '@frontend/sports/content-distribution/feature';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { Observable, first, forkJoin, map, of, switchMap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SportsCacheService {
    private clientCachingConfig = inject(ClientCachingConfig);
    private bettingContent = inject(BettingOfferApi);
    private teamPagesData = inject(TeamPagesModuleConfig);
    private teamPagesConfig = inject(TeamPagesConfig);
    private masterdataApi = inject(MasterdataApiService);

    private liveSportList: ExpiringCache = new ExpiringCache(this.clientCachingConfig.liveSportListTimeout);
    private sportListCache: ExpiringCache = new ExpiringCache(this.clientCachingConfig.sportListTimeout);
    private sportItemCache: ExpiringCache = new ExpiringCache(this.clientCachingConfig.sportTimeout);

    constructor() {
        const userService = inject(UserService);
        const productUtils = inject(SportsProductUtils);

        userService.onAuthenticationChange$.pipe(productUtils.filterSportsEmitLast()).subscribe(() => {
            this.sportListCache.clear();
        });
    }

    getSportList(): Observable<CountItem[]> {
        const source = this.bettingContent.getSportList().pipe(map((result) => sportModelMethods.fromTag(result)));

        return this.sportListCache.getOrCreate('sport-list', source);
    }

    getLiveSportList(): Observable<CountItem[]> {
        const source = this.bettingContent.getLiveSportList().pipe(map((result) => sportModelMethods.fromTag(result)));

        return this.liveSportList.getOrCreate('live-sport-list', source);
    }

    getSport(id: number, competitionId?: number, conferenceId?: number, includeDynamicCategories?: boolean): Observable<CountItem | undefined> {
        const sportSource = forkJoin([this.bettingContent.getSport(id, includeDynamicCategories), this.isTeamPagesEnabled(id)]);

        return this.sportItemCache.getOrCreate(`sport-${id}${includeDynamicCategories ? `-withDynamicCategories` : ''}`, sportSource).pipe(
            switchMap(([tag, team]) =>
                this.getConference(conferenceId, competitionId, undefined, includeDynamicCategories).pipe(
                    map((conference) => {
                        const sport = sportModelMethods.fromTag((tag || []).concat(conference || [])).pop();
                        if (sport) {
                            return {
                                ...sport,
                                meta: {
                                    ...sport.meta,
                                    teamPages: team,
                                },
                            };
                        }

                        return;
                    }),
                ),
            ),
        );
    }

    // TODO: New call to bettingoffer/counts is needed because CDS requires CopmetitionId to process Conferences
    // Revert these changes when CDS starts returning Conferences counts per SportId
    getConference(
        conferenceId?: number,
        competitionId?: number,
        interval?: { from?: string; to?: string },
        includeDynamicCategories?: boolean,
    ): Observable<TagCount[] | undefined> {
        if (!conferenceId || !competitionId) {
            return of(undefined);
        }

        const conference = this.bettingContent.getCountsForConference(conferenceId, competitionId, interval, includeDynamicCategories);

        return interval
            ? conference
            : this.sportItemCache.getOrCreate(
                  `competition-${competitionId}-conference-${conferenceId}${includeDynamicCategories ? `-withDynamicCategories` : ''}`,
                  conference,
              );
    }

    getAllSportsList(): Observable<CountItem[]> {
        return forkJoin([this.getSportList(), this.getLiveSportList()]).pipe(
            map(([latestSports, liveSports]) => {
                const allSports = new Array<CountItem>();
                allSports.push(...latestSports);

                for (let index = 0; index < allSports.length; index++) {
                    if (liveSports.find((x) => x.id === allSports[index].id)) {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        allSports[index] = liveSports.find((x) => x.id === allSports[index].id)!;
                    } else {
                        allSports[index].counts.live = 0;
                    }
                }

                return allSports;
            }),
        );
    }

    private isTeamPagesEnabled(sportId: number) {
        const id = Number(sportId);
        const isTeamPagesEnabledForSport = this.teamPagesConfig.whitelistedSports.includes(id);

        if (this.teamPagesConfig.isEnabled && isTeamPagesEnabledForSport) {
            return this.teamPagesData.whenReady.pipe(
                first(),
                switchMap(() => {
                    const sports = this.teamPagesData.teamPageItems.sportDetails;
                    const competitions = sports?.find((s) => s.sportId === sportId)?.competitionDetails;
                    if (competitions?.length) {
                        return this.masterdataApi.getCompetitionsAndTeamsCount(competitions).pipe(map((p) => p.length > 0));
                    }

                    return of(false);
                }),
            );
        }

        return of(false);
    }
}
